import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import debounce from 'lodash/debounce';
import { SearchIcon } from '@svgs';

import {
  BodyWrapper,
  Header,
  Loader,
  MainWrapper,
  PagesList,
  Message,
} from '@components';
import { usePageTemplates } from '@hooks';
import { businessQuery, pagesQuery, siteQuery } from '@queries';

import { TemplateBreadcrumbs } from '../components/TemplateBreadcrumbs/TemplateBreadcrumbs.jsx';
import { TemplateSidebar } from '../components/TemplateSidebar/TemplateSidebar.jsx';

// Move to Utils?
const matchPage = (page, query) => {
  // determine filter match for a particular Page

  // explicit matching only
  const exactMatchFields = [page.id.toString()];
  if (exactMatchFields.some((v) => v && v.toLowerCase() === query)) {
    return true;
  }

  // string match for fields
  const pageFields = [page.name, page.path, page.title];
  if (pageFields.some((v) => v && v.toLowerCase().indexOf(query) > -1)) {
    return true;
  }

  return false;
};

// Move to Utils?
const filterPages = (pages, query) => {
  // client side filtering of pages
  const cleanedQuery = query.toLowerCase().trim();
  return (
    pages?.filter((page) => {
      if (matchPage(page, cleanedQuery)) {
        return page;
      }
    }) || []
  );
};
export const TemplatePages = () => {
  const { t, i18n } = useTranslation();
  const { businessID, siteID } = useParams();
  // filter search term
  const [searchTerm, setSearchTerm] = useState('');
  // get business
  const { data: businessData } = useQuery(businessQuery(businessID));

  // get site/template
  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  // get pages
  //
  // gcTime set to 0 to always force a re-query in case pages have
  // been updated in other frames (i.e. site builder), etc.
  const { data: pagesData, isPending: pagesIsPending } = useQuery(
    pagesQuery(
      {
        order_by: 'pattern',
        site_id: siteID,
        _fields: ['id', 'site_id','name', 'path', 'type', 'is_draft', 'active', 'noindex', 'custom_field_published', 'full_path', 'custom_page_type',].join(','),
      },
      { gcTime: 0 },
    ),
  );

  const { data: pageTemplates, isPending: pageTemplatesIsPending } =
    usePageTemplates(siteID, siteData?.theme_id);

  const handleFilterChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // https://medium.com/nerd-for-tech/debounce-your-search-react-input-optimization-fd270a8042b
  const debouncedResults = useMemo(() => {
    return debounce(handleFilterChange, 300);
  }, []);

  useEffect(() => {
    return () => {
      debouncedResults.cancel();
    };
  });

  // filter websites if there is a search term, otherwise return the full list
  const fitleredPages = searchTerm
    ? filterPages(pagesData?.objects || [], searchTerm)
    : pagesData?.objects || [];

  // setting pending status
  const isPending = pagesIsPending || siteIsPending || pageTemplatesIsPending;
  return (
    <>
      <Header />
      <BodyWrapper>
        <TemplateSidebar
          businessID={businessID}
          businessData={businessData}
          siteID={siteID}
          siteData={siteData}
        />
        <MainWrapper>
          <TemplateBreadcrumbs businessData={businessData} siteData={siteData}>
            <li className="sb-flex">
              <span>{t('Pages')}</span>
            </li>
          </TemplateBreadcrumbs>
          <div className="sb-manage-content">
            <div className="sb-manage-content-head">
              <h1>{t('Pages')}</h1>
              <div className="sb-button-list-inline">
                <Link
                  to={`/dashboard/projects/${businessID}/templates/${siteID}/pages/new`}
                  className="sb-btn sb-btn-cancel"
                >
                  Add new
                </Link>
              </div>
            </div>
            <div className="sb-manage-options-menu sb-flex">
              <div className="sb-manage-options-left">
                <div className="sb-flex">
                  <form
                    role="search"
                    className="sb-manage-options-search"
                    onSubmit={(e) => e.preventDefault()}
                  >
                    <input
                      type="search"
                      name="query"
                      placeholder="Filter"
                      aria-label="Enter search term"
                      autoComplete="off"
                      defaultValue={searchTerm}
                      onChange={debouncedResults}
                      disabled={pagesIsPending}
                    />
                    <SearchIcon />
                  </form>
                </div>
              </div>
              <div className="sb-manage-options-right"></div>
            </div>
            {isPending && <Loader className="sb-loader sb-loader-large" />}
            {pagesData && siteData && (
              <>
                <PagesList
                  pages={fitleredPages}
                  site={siteData}
                  pageTemplates={pageTemplates}
                  mode="template"
                />
                {!isPending && searchTerm && (
                  <Message
                    messageStyle="notice"
                    title={
                      `Filtered to ${fitleredPages.length} of ${pagesData?.objects?.length} page` +
                      (pagesData?.objects?.length != 1 ? 's' : '')
                    }
                    fadeoutMessage={false}
                    isFormMessage={false}
                  />
                )}
              </>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
